import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Brand from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

export default function AdminRecover() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/recovery/request", { email });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="recover-page">
      <header className="px-6 sm:px-12 pt-10 max-w-6xl w-full mx-auto">
        <Link to="/"><Brand size="sm" subtitle={true} /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <ShieldAlert className="w-8 h-8 text-primary mx-auto mb-3" />
            <h1 className="font-display text-4xl">Recover access</h1>
            <p className="text-sm text-muted-foreground mt-3">
              Lost access to your authenticator? Request a one-time recovery link.
              The link is sent to the superadmin for review.
            </p>
          </div>
          <div className="border border-border rounded-2xl bg-card p-7">
            {done ? (
              <div className="text-center space-y-4" data-testid="recover-done">
                <p className="text-sm">
                  If <span className="text-primary">{email}</span> is a valid account, a recovery
                  link was sent to the superadmin email.
                </p>
                <Link
                  to="/admin/login"
                  className="inline-block px-5 py-2 rounded-full border border-primary/40 text-primary hover:bg-primary/10"
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="recover-form">
                <div className="space-y-2">
                  <Label htmlFor="email">Account email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="recover-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="recover-submit-button"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request recovery"}
                </Button>
                <div className="text-center text-xs">
                  <Link to="/admin/login" className="text-muted-foreground hover:text-primary">
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
